// ─────────────────────────────────────────────────────────────
// CalendarButtons.js
// Compact inline-link calendar section.
// ─────────────────────────────────────────────────────────────

import React from 'react';
import { trackCalendarGoogle, trackCalendarDownload } from '../utils/analytics';
import {
  generateSingleEventICS,
  generateSeriesICS,
  googleCalendarUrl,
  downloadICS,
} from '../utils/calendarUtils';

export default function CalendarButtons({
  selectedEvent,
  location,
  recurringSettings,
  defaultLocation,
  siteUrl,
  calendarDescription,
  showSingle = true,
  showSeries = true,
  dark = false,
  showToast,
  hideLabel = false,
}) {
  const hasRecurring = recurringSettings && recurringSettings.enabled;

  const handleSingleGoogle = (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    trackCalendarGoogle('single');
    const url = googleCalendarUrl(selectedEvent, location, siteUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSingleApple = (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    trackCalendarDownload('single');
    const ics = generateSingleEventICS(selectedEvent, location, siteUrl, calendarDescription);
    if (!ics) return;
    const title = (selectedEvent.title || 'game').replace(/\s+/g, '-').toLowerCase();
    downloadICS(ics, `${title}.ics`);
  };

  const handleSeries = (e) => {
    e.preventDefault();
    if (!hasRecurring) {
      showToast && showToast('Recurring schedule not configured', 'error');
      return;
    }
    trackCalendarDownload('series');
    const ics = generateSeriesICS(recurringSettings, defaultLocation, siteUrl, calendarDescription);
    if (!ics) return;
    downloadICS(ics, 'pickup-ultimate-schedule.ics');
    showToast && showToast('Open the file to import. Google Calendar: Other calendars \u2192 Import');
  };

  const labelStyle = {
    fontSize: '11px',
    fontWeight: 500,
    color: dark ? 'rgba(255,255,255,.4)' : '#9aaab8',
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    display: 'block',
    marginBottom: '5px',
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '4px',
  };

  const rowLabelStyle = {
    fontSize: '12px',
    color: dark ? 'rgba(255,255,255,.4)' : '#9aaab8',
    minWidth: '76px',
    flexShrink: 0,
  };

  const linkStyle = {
    fontSize: '12px',
    color: dark ? 'rgba(120,180,255,.8)' : 'var(--blue)',
    textDecoration: 'none',
    cursor: 'pointer',
    lineHeight: 1.4,
    background: 'none',
    border: 'none',
    borderBottom: dark
      ? '0.5px solid rgba(120,180,255,.3)'
      : '0.5px solid rgba(45,109,168,.3)',
    padding: 0,
    fontFamily: 'inherit',
  };

  const sepStyle = {
    fontSize: '11px',
    color: dark ? 'rgba(255,255,255,.2)' : '#c8d4de',
  };

  const showSingleGame = showSingle && selectedEvent;
  const showSchedule = showSeries && hasRecurring;

  if (!showSingleGame && !showSchedule) return null;

  return (
    <div>
      {!hideLabel && <span style={labelStyle}>Add to calendar</span>}

      {showSingleGame && (
        <div style={rowStyle}>
          <span style={rowLabelStyle}>This game</span>
          <button style={linkStyle} onClick={handleSingleGoogle}>Google</button>
          <span style={sepStyle}>&middot;</span>
          <button style={linkStyle} onClick={handleSingleApple}>Apple / Outlook</button>
        </div>
      )}

      {showSchedule && (
        <div style={rowStyle}>
          <span style={rowLabelStyle}>{showSingleGame ? 'Full schedule' : 'Our schedule'}</span>
          <button style={linkStyle} onClick={handleSeries}>All calendars (.ics)</button>
        </div>
      )}
    </div>
  );
}
